import React, { Component, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState(prev => ({ errorCount: prev.errorCount + 1 }));
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div
          style={{
            padding: '12px 16px',
            margin: '4px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#fca5a5',
            fontSize: '13px',
            textAlign: 'center',
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: 500 }}>
            渲染出错
          </div>
          <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '8px' }}>
            {this.state.error?.message || '未知错误'}
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                background: 'rgba(239, 68, 68, 0.15)',
                color: '#fca5a5',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              重试
            </button>
            {this.state.errorCount >= 2 && (
              <button
                onClick={this.handleReload}
                style={{
                  padding: '4px 12px',
                  borderRadius: '4px',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  background: 'rgba(239, 68, 68, 0.25)',
                  color: '#fca5a5',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                刷新页面
              </button>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export class NodeErrorBoundary extends Component<
  { children: ReactNode; nodeId: string; nodeName?: string },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode; nodeId: string; nodeName?: string }) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[NodeErrorBoundary] Node ${this.props.nodeId} crashed:`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            width: '200px',
            padding: '12px',
            borderRadius: '8px',
            background: 'rgba(30, 30, 40, 0.95)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            color: '#fca5a5',
            fontSize: '12px',
            textAlign: 'center',
          }}
        >
          <div style={{ marginBottom: '6px', fontWeight: 500 }}>
            ⚠️ {this.props.nodeName || '节点'}渲染失败
          </div>
          <div style={{ fontSize: '10px', opacity: 0.6, marginBottom: '8px', wordBreak: 'break-all' }}>
            {this.state.error?.message || '未知错误'}
          </div>
          <button
            onClick={this.handleReset}
            style={{
              padding: '3px 10px',
              borderRadius: '4px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#fca5a5',
              cursor: 'pointer',
              fontSize: '11px',
            }}
          >
            重试
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
